import Dialog from "@rio-cloud/rio-uikit/Dialog";
import { useT } from "@hooks";
import type { RecurrenceScope } from "@types";

type RecurrenceScopeMode = "edit" | "delete";

interface RecurrenceScopeDialogProps {
  show: boolean;
  mode: RecurrenceScopeMode;
  isSubmitting?: boolean;
  onClose: () => void;
  onConfirm: (scope: RecurrenceScope) => void;
}

const ICON_SIZE = 50;
const CIRCLE_SIZE = 72;

const CIRCLE_STYLE = {
  width: CIRCLE_SIZE,
  height: CIRCLE_SIZE,
  flex: "0 0 auto",
} as const;

const ICON_STYLE = { fontSize: ICON_SIZE } as const;

const MODE_CONFIG: Record<
  RecurrenceScopeMode,
  {
    titleKey: string;
    subtitleKey: string;
    iconClass: string;
    circleClass: string;
    buttonClass: string;
    outlineClass: string;
  }
> = {
  edit: {
    titleKey: "recurrence.dialog.editScopeTitle",
    subtitleKey: "recurrence.dialog.editScopeSubtitle",
    iconClass: "rioglyph-warning-sign",
    circleClass: "bg-warning",
    buttonClass: "btn btn-primary",
    outlineClass: "btn btn-primary btn-outline",
  },
  delete: {
    titleKey: "recurrence.dialog.deleteScopeTitle",
    subtitleKey: "recurrence.dialog.deleteScopeSubtitle",
    iconClass: "rioglyph-warning-sign",
    circleClass: "bg-danger",
    buttonClass: "btn btn-danger",
    outlineClass: "btn btn-danger btn-outline",
  },
};

const OPTION_LABEL_KEY: Record<RecurrenceScopeMode, Record<RecurrenceScope, string>> = {
  edit: {
    ALL_GROUP: "recurrence.option.editAll",
    SINGLE: "recurrence.option.editSingle",
    FUTURE_AND_PENDING: "recurrence.option.editFuture",
  },
  delete: {
    ALL_GROUP: "recurrence.option.deleteAll",
    SINGLE: "recurrence.option.deleteSingle",
    FUTURE_AND_PENDING: "recurrence.option.deleteFuture",
  },
};

const RecurrenceScopeDialog = ({
  show,
  mode,
  isSubmitting = false,
  onClose,
  onConfirm,
}: RecurrenceScopeDialogProps) => {
  const t = useT();
  const config = MODE_CONFIG[mode];

  return (
    <Dialog
      show={show}
      bsSize="md"
      title={<span />}
      body={
        <div className="padding-y-25 display-flex flex-column align-items-center text-center gap-25">
          <div
            className={`display-flex align-items-center justify-content-center rounded-circle ${config.circleClass}`}
            style={CIRCLE_STYLE}
          >
            <span className={`rioglyph ${config.iconClass} text-color-white`} style={ICON_STYLE} aria-hidden="true" />
          </div>

          <div className="display-flex flex-column gap-5">
            <span className="text-size-20 text-bold">{t(config.titleKey)}</span>
            <span className="text-size-16">{t(config.subtitleKey)}</span>
            {mode === "delete" && (
              <span className="text-size-14 text-color-danger">{t("common.dialog.irreversibleAction")}</span>
            )}
          </div>

          <div className="display-flex flex-wrap justify-content-center gap-10 width-100pct">
            <button
              type="button"
              className="btn btn-default text-color-gray border-color-light hover-bg-lighter hover-text-color-gray hover-border-color-light width-100pct-xs"
              disabled={isSubmitting}
              onClick={() => onConfirm("ALL_GROUP")}
            >
              {t(OPTION_LABEL_KEY[mode].ALL_GROUP)}
            </button>
            <button
              type="button"
              className={`${config.outlineClass} width-100pct-xs`}
              disabled={isSubmitting}
              onClick={() => onConfirm("SINGLE")}
            >
              {t(OPTION_LABEL_KEY[mode].SINGLE)}
            </button>
            <button
              type="button"
              className={`${config.buttonClass} width-100pct-xs`}
              disabled={isSubmitting}
              onClick={() => onConfirm("FUTURE_AND_PENDING")}
            >
              {t(OPTION_LABEL_KEY[mode].FUTURE_AND_PENDING)}
            </button>
          </div>
        </div>
      }
      onClose={onClose}
      showCloseButton
      useOverflow
    />
  );
};

export default RecurrenceScopeDialog;
